import { useEffect, useState } from 'react';
import { Anchor, Button, Group, Modal, Stack, Switch, Text } from '@mantine/core';
import { useClarity } from '../hooks/useClarity';

export interface AnalyticsSettingsModalProps {
  opened: boolean;
  onClose: () => void;
}

export function AnalyticsSettingsModal({opened, onClose}: AnalyticsSettingsModalProps) {
  const {consent, grantConsent, denyConsent} = useClarity();
  const [analyticsConsent, setAnalyticsConsent] = useState(consent?.analytics_Storage === 'granted');

  // Sync local state when consent changes or modal opens
  useEffect(() => {
    if (consent) {
      setAnalyticsConsent(consent.analytics_Storage === 'granted');
    }
  }, [consent, opened]);

  const handleSave = async () => {
    if (analyticsConsent) {
      await grantConsent({
        analytics_Storage: 'granted',
      });
    } else {
      await denyConsent();
    }
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Analytics Settings"
      size="md"
      aria-label="analytics-settings-modal"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Configure analytics preferences to help us improve CadCare Connect. We use Microsoft Clarity to understand how you use the application.{' '}
          <Anchor
            href="https://www.microsoft.com/en-us/privacy/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            aria-label="microsoft-privacy-statement-link"
          >
            Microsoft Privacy Statement
          </Anchor>
          {' | '}
          <Anchor
            href="/dpa/"
            size="sm"
            aria-label="our-dpa-link"
          >
            Data Processing Addendum
          </Anchor>
        </Text>

        <Switch
          label="Analytics Cookies"
          description="Help us improve by allowing analytics tracking"
          checked={analyticsConsent}
          onChange={(event) => setAnalyticsConsent(event.currentTarget.checked)}
          aria-label="analytics-consent-switch"
        />

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={handleClose}
            aria-label="cancel-analytics-settings"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            aria-label="save-analytics-settings"
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
